import { motion, useScroll, useSpring } from "framer-motion";

/* =========================
   Animation Variants
========================= */

const cardVariants = {
  hidden: (direction) => ({
    opacity: 0,
    x: direction === "left" ? -50 : 50,
    filter: "blur(10px)",
  }),
  show: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

/* =========================
   Step Component
========================= */

function Step({ title, description, date, direction }) {
  const isLeft = direction === "left";

  return (
    <div className={`relative flex w-full mb-24 justify-between items-center ${isLeft ? "flex-row" : "flex-row-reverse"}`}>
      {/* Card Container */}
      <motion.div
        custom={direction}
        variants={cardVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.5 }}
        className="w-[45%] group backdrop-blur-md"
      >
        <div className="relative rounded-3xl hover:from-blue-500/40 transition-all duration-500">
          <div className="border border-white/10 backdrop-blur-xl rounded-[22px] p-6 group-hover:border-slate-700 transition-colors">
            <span className="text-slate-400 font-mono text-sm font-bold mb-2 block">{date}</span>
            <h1 className="text-2xl font-bold tracking-tight text-white mb-2 italic">
              {title}
            </h1>
            <p className="text-slate-400 leading-relaxed font-medium">
              {description}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Central Dot */}
      <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center z-10">
        <motion.div 
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          className="h-4 w-4 rounded-full bg-slate-300 ring-2 ring-slate-400 shadow-[0_0_15px_rgba(59,130,246,0.5)]" 
        />
      </div>

      {/* Empty space for the other side */}
      <div className="w-[45%]" />
    </div>
  );
}

/* =========================
   Education Component
========================= */

export default function Education({ REF }) {  
  // Create a scroll-linked progress bar
  const { scrollYProgress } = useScroll({
    target: REF,
    offset: ["start end", "end end"],
  });

  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const educationData = [
    {
      title: "1337 Common Core",
      date: "2024",
      direction: "left",
      description: "Completed common core training as a software engineer at level 11.09, specializing in system programming and low-level algorithms."
    },
    {
      title: "DEUG Science Physics",
      date: "2022",
      direction: "right",
      description: "Deepened analytical thinking and mathematical foundations at Faculty of Sciences, Semlalia Marrakech."
    },
    {
      title: "Baccalaureate",
      date: "2019",
      direction: "left",
      description: "Successfully graduated in Physical Sciences with honors, marking the beginning of my technical journey."
    }
  ];

  return (
    <div ref={REF} className="font-poppins relative min-h-screen py-24 px-4 overflow-hidden">
      {/* Section Header */}
      <div className="text-center mb-20">
            <h2 className="text-xl md:text-3xl font-bold text-slate-300 tracking-[0.2em] uppercase">
                Educations
            </h2>
      </div>

      <div className="max-w-6xl mx-auto relative">
        {/* The Animated Line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-slate-800 -translate-x-1/2">
          <motion.div
            style={{ scaleY, originY: 0 }}
            className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-b from-slate-600 via-cyan-400 to-transparent shadow-[0_0_10px_#3b82f6]"
          />
        </div>

        {/* Timeline Steps */}
        <div className="relative">
          {educationData.map((item, index) => (
            <Step key={index} {...item} />
          ))}
        </div>
      </div>
    </div>
  );
}